/**
 * The few shapes the contact profile is built from.
 *
 * The old profile drew every section the same way (a kicker, a hairline, a
 * label/value grid), so nothing led and a reader could not tell the card that
 * matters from the one that is housekeeping. The profile now has three
 * weights and these are them:
 *
 *   - `Card`     reference a desk reads the contact by: how to reach, track
 *                record, what we know, the timeline. Boxed.
 *   - `Quiet`    housekeeping a desk looks up rarely: record details, news,
 *                the record's history. A hairline and a small title, no box.
 *   - the next-best-action card (its own component), the only gold outline.
 *
 * Gold is the app's own ramp (`amber-*`, tailwind.config.js), the same one the
 * rest of the CRM uses, not the corporate #FFD86B.
 */
import { useState, type ReactNode } from 'react'
import clsx from 'clsx'
import { ChevronRight } from 'lucide-react'

/** The focus ring every control in the profile draws. */
export const FOCUS =
  'focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-amber-300/80'

/** A text-link in running copy. */
export const LINK =
  'rounded text-stone-100 underline decoration-stone-600 decoration-dotted underline-offset-4 hover:text-amber-200 hover:decoration-amber-300/60 ' +
  FOCUS

/** A quiet button: bordered, never filled. */
export const QUIET_BUTTON =
  'inline-flex items-center justify-center gap-1.5 rounded-md border border-stone-700 bg-black/30 px-3 py-1.5 text-[12px] font-medium text-stone-200 hover:border-amber-400/40 hover:text-stone-50 disabled:cursor-not-allowed disabled:opacity-45 ' +
  FOCUS

export const KICKER = 'font-mono text-[10px] uppercase tracking-[0.16em] text-stone-500'

/** A boxed card. `title` is an h3. */
export function Card({
  title,
  kicker = false,
  action,
  children,
  className,
  label,
}: {
  title: string
  /** Mono small-caps title, for the side columns; plain semibold otherwise. */
  kicker?: boolean
  action?: ReactNode
  children: ReactNode
  className?: string
  label?: string
}) {
  return (
    <section
      aria-label={label ?? title}
      className={clsx('rounded-xl border border-stone-800 bg-black/35 px-4 py-4', className)}
    >
      <div className="mb-3 flex items-baseline justify-between gap-3">
        <h3 className={kicker ? KICKER : 'font-sans text-[14px] font-semibold text-stone-100'}>{title}</h3>
        {action}
      </div>
      {children}
    </section>
  )
}

/** Housekeeping: a hairline above, a small title, no box. */
export function Quiet({
  title,
  action,
  children,
  className,
}: {
  title: string
  action?: ReactNode
  children: ReactNode
  className?: string
}) {
  return (
    <section aria-label={title} className={clsx('border-t border-stone-800/80 pt-3', className)}>
      <div className="mb-2 flex items-baseline justify-between gap-3">
        <h3 className="font-mono text-[10px] uppercase tracking-[0.16em] text-stone-600">{title}</h3>
        {action}
      </div>
      {children}
    </section>
  )
}

/** A label over a value, with an optional line saying where the value came from. */
export function Field({
  label,
  children,
  note,
}: {
  label: string
  children: ReactNode
  note?: ReactNode
}) {
  return (
    <div className="min-w-0">
      <div className="text-[11px] text-stone-500">{label}</div>
      <div className="mt-0.5 min-w-0 break-words text-[13px] font-medium text-stone-100">{children}</div>
      {note ? <div className="mt-1 text-[11px] leading-snug text-stone-500">{note}</div> : null}
    </div>
  )
}

export const ABSENT_TEXT = 'Not on record'

export function Absent({ children = ABSENT_TEXT }: { children?: ReactNode }) {
  return <span className="font-normal italic text-stone-500">{children}</span>
}

/** Two letters in a circle, or an icon in a rounded square for an organization. */
export function Avatar({
  children,
  size = 'md',
  square = false,
}: {
  children: ReactNode
  size?: 'sm' | 'md' | 'lg'
  square?: boolean
}) {
  return (
    <span
      aria-hidden="true"
      className={clsx(
        'flex shrink-0 items-center justify-center border border-stone-700 bg-stone-900 font-semibold text-stone-200',
        square ? 'rounded-lg' : 'rounded-full',
        size === 'lg' && 'h-[72px] w-[72px] text-[24px]',
        size === 'md' && 'h-9 w-9 text-[12px]',
        size === 'sm' && 'h-7 w-7 text-[10px]',
      )}
    >
      {children}
    </span>
  )
}

/** A collapsed block: a hairline, a small title, and a button that opens it. */
export function Disclosure({ title, children, className }: { title: string; children: ReactNode; className?: string }) {
  const [open, setOpen] = useState(false)
  return (
    <section aria-label={title} className={clsx('border-t border-stone-800/80 pt-3', className)}>
      <button
        type="button"
        aria-expanded={open}
        onClick={() => setOpen((v) => !v)}
        className={clsx('flex w-full items-center justify-between gap-2 rounded text-left', KICKER, 'hover:text-stone-300', FOCUS)}
      >
        <span>{title}</span>
        <ChevronRight size={12} className={clsx('transition-transform', open && 'rotate-90')} aria-hidden />
      </button>
      {open ? <div className="mt-3">{children}</div> : null}
    </section>
  )
}
